import React, { useEffect, useRef, useState } from "react";
import {
  Container, Card, CardBody, Row, Col, Badge, Input, Label, Button,
  Spinner, FormGroup, Alert,
} from "reactstrap";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  getPropertyTicketById, updatePropertyTicket, addPropertyTicketMessage,
  onPropertyTicketMessages, notifyPropertyTicketParticipants,
} from "../../../helpers/firestoreService";
import { useAuthUser } from "../../../hooks/AuthProvider";

const SEVERITY_COLORS = { Low: "info", Medium: "warning", High: "orange", Urgent: "danger" };
const STATUS_COLORS = { Open: "primary", "In Progress": "warning", Resolved: "success", Closed: "secondary" };

const TicketDetail = () => {
  document.title = "Ticket Detail | Support Portal";

  const { ticketId } = useParams();
  const navigate = useNavigate();
  const user = useAuthUser();

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [adminNote, setAdminNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const t = await getPropertyTicketById(ticketId);
        if (!t) { navigate("/tickets"); return; }
        setTicket(t);
        setStatus(t.status);
        setAdminNote(t.adminNote || "");
      } catch (e) {
        console.error("Load ticket error:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [ticketId, navigate]);

  useEffect(() => {
    if (!ticketId) return;
    const unsub = onPropertyTicketMessages(ticketId, setMessages);
    return () => unsub();
  }, [ticketId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const notifyOthers = (type, title, message) => {
    if (!ticket?.propertyId) return;
    notifyPropertyTicketParticipants(ticket.propertyId, {
      type, title, message, ticketId, excludeUid: user.uid,
    }).catch((e) => console.error("Notify ticket participants error:", e));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updatePropertyTicket(ticketId, { status, adminNote });
      setTicket((prev) => ({ ...prev, status, adminNote }));
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      notifyOthers("ticket_status", "Ticket updated", `Your ticket "${ticket.title}" is now ${status}.`);
    } catch (e) {
      console.error("Update ticket error:", e);
    } finally {
      setSaving(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sendingMessage) return;
    setSendingMessage(true);
    try {
      await addPropertyTicketMessage(ticketId, {
        senderUid: user.uid,
        senderName: user.displayName || user.email,
        senderEmail: user.email,
        text: newMessage.trim(),
      });
      notifyOthers("ticket_message", "New reply on a ticket", `Your property manager replied on "${ticket.title}".`);
      setNewMessage("");
    } catch (e) {
      console.error("Send message error:", e);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleCopyLink = () => {
    const url = `${window.location.origin}/ticket/${ticketId}`;
    navigator.clipboard.writeText(url).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  if (loading) {
    return (
      <div className="page-content text-center py-5">
        <Spinner color="primary" />
      </div>
    )
  }

  if (!ticket) return null

  return (
    <div className="page-content">
      <Container fluid>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <Link to="/tickets" className="text-decoration-none">
            <i className="bx bx-arrow-back me-1" />Back to Tickets
          </Link>
          <Button color="secondary" outline size="sm" onClick={handleCopyLink}>
            <i className="bx bx-link me-1" />{linkCopied ? "Link copied!" : "Copy public link"}
          </Button>
        </div>

        {saveSuccess && <Alert color="success" className="py-2">Ticket updated.</Alert>}

        <Row className="g-4">
          <Col md={5}>
            <Card className="border-0 shadow-sm mb-4">
              <CardBody>
                <h5 className="mb-1">{ticket.title}</h5>
                <div className="d-flex gap-2 mb-3">
                  <Badge color={SEVERITY_COLORS[ticket.severity] || "secondary"} pill>{ticket.severity || "Low"}</Badge>
                  <Badge color={STATUS_COLORS[ticket.status] || "secondary"} pill>{ticket.status}</Badge>
                </div>
                <p className="mb-3">{ticket.description}</p>
                <div className="text-muted small mb-3">
                  <div><i className="bx bx-user me-1" />{ticket.tenantName || ticket.createdByEmail || "—"}</div>
                  {ticket.propertyAddress && (
                    <div>
                      <i className="bx bx-map-pin me-1" />
                      {ticket.propertyId ? (
                        <Link to={`/properties/${ticket.propertyId}`}>{ticket.propertyAddress}</Link>
                      ) : ticket.propertyAddress}
                    </div>
                  )}
                  <div><i className="mdi mdi-clock-outline me-1" />{ticket.createdAt ? new Date(ticket.createdAt).toLocaleString() : "—"}</div>
                </div>

                <hr />
                <FormGroup>
                  <Label className="small fw-semibold">Status</Label>
                  <Input type="select" value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="Open">Open</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Resolved">Resolved</option>
                    <option value="Closed">Closed</option>
                  </Input>
                </FormGroup>
                <FormGroup>
                  <Label className="small fw-semibold">Internal Note</Label>
                  <Input type="textarea" rows={3} value={adminNote} onChange={(e) => setAdminNote(e.target.value)} />
                </FormGroup>
                <Button color="primary" size="sm" onClick={handleSave} disabled={saving}>
                  {saving ? <Spinner size="sm" /> : "Save"}
                </Button>
              </CardBody>
            </Card>
          </Col>

          <Col md={7}>
            <Card className="border-0 shadow-sm">
              <CardBody>
                <h6 className="fw-semibold mb-3 text-muted text-uppercase" style={{ fontSize: "0.72rem", letterSpacing: "0.05em" }}>
                  <i className="bx bx-chat me-1" />Conversation
                </h6>
                <div style={{ maxHeight: 420, overflowY: "auto" }} className="mb-3">
                  {messages.length === 0 ? (
                    <p className="text-muted small fst-italic mb-0">No messages yet.</p>
                  ) : (
                    messages.map((m) => (
                      <div key={m.id} className={`d-flex mb-2 ${m.senderUid === user.uid ? "justify-content-end" : ""}`}>
                        <div
                          className={`p-2 rounded ${m.senderUid === user.uid ? "bg-primary text-white" : "bg-light"}`}
                          style={{ maxWidth: "75%" }}
                        >
                          <div className="small fw-semibold mb-1">{m.senderName || "Anonymous"}</div>
                          <div className="small">{m.text}</div>
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={chatEndRef} />
                </div>
                <form className="d-flex gap-2" onSubmit={handleSendMessage}>
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message…"
                    disabled={sendingMessage}
                  />
                  <Button color="primary" type="submit" disabled={sendingMessage || !newMessage.trim()}>
                    {sendingMessage ? <Spinner size="sm" /> : <i className="bx bx-send" />}
                  </Button>
                </form>
                <small className="text-muted d-block mt-2" style={{ fontSize: "0.7rem" }}>
                  By using this chat, you agree to our{" "}
                  <a href="/community-guidelines" target="_blank" rel="noopener noreferrer">
                    Community Guidelines
                  </a>.
                </small>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default TicketDetail;

